<template>
  <v-container class="pa-0">
    <v-tooltip v-if="!hideActivator" bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          style="width: auto; height: auto"
          icon
          v-bind="attrs"
          v-on="on"
          @click="openDialog"
        >
          <my-icon
            :name="'file-upload-icon'"
            :fill="$vuetify.theme.isDark ? '#fff' : '#000'"
            :width="18"
            :height="18"
            :cursor-pointer="true"
          />
        </v-btn>
      </template>
      <span>{{ title }}</span>
    </v-tooltip>

    <v-dialog
      v-model="dialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="940"
      @click:outside="closeDialog"
    >
      <v-card
        class="file-process-card"
        :class="{ 'file-process-card--mobile': isMobile }"
      >
        <div class="file-process-header d-flex align-center justify-space-between">
          <div class="file-process-title text-right">
            {{ title }}
          </div>
          <v-btn icon small color="grey darken-2" @click="closeDialog">
            <v-icon class="close-hover" small>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider />

        <div class="file-process-body">
          <div class="file-process-toolbar d-flex align-center">
            <div class="file-process-toolbar-actions d-flex align-center">
              <div
                class="file-process-search-shell"
                :class="{
                  'is-expanded': searchExpanded,
                  'is-focused': searchFocused,
                  'has-value': !!searchQuery,
                }"
              >
                <button
                  type="button"
                  class="file-process-search-toggle"
                  :title="$t('search')"
                  @click="expandSearch"
                >
                  <v-icon
                    size="18"
                    :color="searchExpanded || searchFocused ? '#0066FF' : '#607d8b'"
                  >
                    mdi-magnify
                  </v-icon>
                </button>
                <input
                  ref="searchInput"
                  v-model="searchQuery"
                  type="text"
                  class="file-process-search-input"
                  :placeholder="$t('search')"
                  @focus="searchFocused = true"
                  @blur="onSearchBlur"
                />
                <button
                  v-if="searchExpanded && searchQuery"
                  type="button"
                  class="file-process-search-clear"
                  @mousedown.prevent
                  @click="clearSearch"
                >
                  <v-icon size="16" color="#666">mdi-close</v-icon>
                </button>
              </div>

              <v-menu offset-y left content-class="file-process-sort-menu-content">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    type="button"
                    class="file-process-sort-btn"
                    :class="{ 'has-type': filterType !== 'all' }"
                    :disabled="uploading"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="16" :color="filterType !== 'all' ? '#1976d2' : '#607d8b'">
                      mdi-sort
                    </v-icon>
                    <span
                      class="file-process-sort-label text-truncate"
                      :class="{ 'is-typed': filterType !== 'all' }"
                    >
                      {{ $t(filterType) }}
                    </span>
                    <v-icon size="14" color="#90a4ae">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list dense class="file-process-sort-menu">
                  <v-list-item
                    :class="{ 'is-active': filterType === 'all' }"
                    @click="selectFilterType('all')"
                  >
                    <v-list-item-title>{{ $t("all") }}</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-for="item in visibleImportTypes"
                    :key="item.value"
                    :disabled="!item.enabled"
                    :class="{ 'is-active': filterType === item.value }"
                    @click="selectFilterType(item.value)"
                  >
                    <v-list-item-title>{{ $t(item.value) }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <div
                v-if="filterType !== 'all'"
                class="file-process-import-wrap d-flex align-center"
              >
                <button
                  v-if="!lastAttachedName"
                  type="button"
                  class="file-process-import-btn"
                  :disabled="uploading"
                  :title="$t('uploadFile')"
                  @click="openUploadDialog"
                >
                  <v-progress-circular
                    v-if="uploading || isSelecting"
                    indeterminate
                    size="16"
                    width="2"
                    color="#1976d2"
                  />
                  <v-icon v-else size="18" color="#1976d2">mdi-upload</v-icon>
                </button>
                <div
                  v-else
                  class="file-process-attached-chip d-flex align-center"
                >
                  <button
                    type="button"
                    class="file-process-attached-main d-flex align-center"
                    :disabled="uploading"
                    :title="lastAttachedName"
                    @click="openUploadDialog"
                  >
                    <span class="file-process-import-mark">
                      <v-icon size="10">mdi-check</v-icon>
                    </span>
                    <v-icon size="15" color="#546e7a" class="ml-1">mdi-paperclip</v-icon>
                    <span class="file-process-attached-name text-truncate">
                      {{ lastAttachedName }}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="file-process-import-clear"
                    :title="$t('remove')"
                    @click="clearAttachedFile"
                  >
                    <v-icon size="14" color="#78909c">mdi-close</v-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <input
            ref="uploader"
            class="d-none"
            type="file"
            accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            @change="onFileChanged"
          />

          <div v-if="items.length" class="file-process-active">
            <div class="file-process-kpis d-flex align-stretch flex-wrap">
              <div class="file-process-kpi d-flex align-center">
                <span class="file-process-kpi-icon file-process-kpi-icon--success">
                  <v-icon x-small color="#2e7d32">mdi-check</v-icon>
                </span>
                <span class="file-process-kpi-label">{{ $t("SUCCESS") }}</span>
                <span class="file-process-kpi-value">{{ listSummary.success }}</span>
              </div>
              <div class="file-process-kpi d-flex align-center">
                <span class="file-process-kpi-icon file-process-kpi-icon--queue">
                  <v-icon x-small color="#ef6c00">mdi-clock-outline</v-icon>
                </span>
                <span class="file-process-kpi-label">{{ $t("queue") }}</span>
                <span class="file-process-kpi-value">{{ listSummary.pending }}</span>
              </div>
              <div class="file-process-kpi d-flex align-center">
                <span class="file-process-kpi-icon file-process-kpi-icon--failed">
                  <v-icon x-small color="#c62828">mdi-alert-circle-outline</v-icon>
                </span>
                <span class="file-process-kpi-label">{{ $t("failed") }}</span>
                <span class="file-process-kpi-value">{{ listSummary.failed }}</span>
              </div>
              <div
                v-if="listSummary.cancelled"
                class="file-process-kpi d-flex align-center"
              >
                <span class="file-process-kpi-icon file-process-kpi-icon--cancelled">
                  <v-icon x-small color="#546e7a">mdi-cancel</v-icon>
                </span>
                <span class="file-process-kpi-label">{{ $t("cancelled") }}</span>
                <span class="file-process-kpi-value">{{ listSummary.cancelled }}</span>
              </div>
            </div>
          </div>

          <div class="file-process-panel">
            <div class="file-process-list-head d-flex align-center justify-space-between">
              <div class="file-process-list-title mb-0">
                {{ $t("importData") }}
                <template v-if="filterType !== 'all'">
                  — {{ $t(filterType) }}
                </template>
              </div>
              <div
                v-if="items.length"
                class="file-process-list-head-actions d-flex align-center"
              >
                <div class="file-process-list-count">
                  {{ filteredItems.length }} {{ $t("case") }}
                </div>
                <v-btn
                  color="error"
                  outlined
                  x-small
                  height="26"
                  :disabled="!selectedIds.length"
                  :loading="cancelling"
                  @click="cancelImport"
                >
                  <v-icon left x-small>mdi-stop</v-icon>
                  {{ $t("stop") }}
                </v-btn>
              </div>
            </div>

            <div
              v-if="loadingList"
              class="file-process-loading d-flex align-center justify-center"
            >
              <v-progress-circular indeterminate size="22" width="2" color="primary" />
            </div>

            <div
              v-else-if="!filteredItems.length"
              class="file-process-empty text-center d-flex align-center justify-center"
            >
              {{ $t("no-data-found") }}
            </div>

            <div v-else class="file-process-panel-body">
              <div class="file-process-list">
                <v-simple-table dense class="file-process-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="file-process-col-check">
                          <v-checkbox
                            :input-value="allSelected"
                            :indeterminate="someSelected"
                            hide-details
                            dense
                            class="ma-0 pa-0 file-process-check"
                            @click.stop.prevent="toggleSelectAll"
                          />
                        </th>
                        <th class="file-process-col-index">#</th>
                        <th class="file-process-col-file">{{ $t("fileName") }}</th>
                        <th class="file-process-col-percent">
                          {{ $t("percent") }} {{ $t("progress") }}
                        </th>
                        <th class="file-process-col-count">{{ $t("doneCountItem") }}</th>
                        <th class="file-process-col-user">{{ $t("registerar") }}</th>
                        <th class="file-process-col-date">{{ $t("uploadDate") }}</th>
                        <th class="file-process-col-status">{{ $t("status") }}</th>
                        <th class="file-process-col-errors">{{ $t("errors") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in pagedItems"
                        :key="item.id"
                        :class="{ 'file-process-row-selected': isRowSelected(item) }"
                        @click="toggleRow(item)"
                      >
                        <td class="file-process-col-check" @click.stop>
                          <v-checkbox
                            :input-value="isRowSelected(item)"
                            hide-details
                            dense
                            class="ma-0 pa-0 file-process-check"
                            @click.stop.prevent="toggleRow(item)"
                          />
                        </td>
                        <td class="file-process-col-index">
                          {{ pageFrom + index }}
                        </td>
                        <td class="file-process-col-file">
                          <div class="file-process-file-name text-truncate">
                            {{ itemFileTitle(item) }}
                          </div>
                        </td>
                        <td class="file-process-col-percent">
                          <div class="file-process-progress">
                            <div class="file-process-progress-meta d-flex align-center justify-end">
                              <span class="file-process-progress-percent">
                                {{ itemPercent(item) }}%
                              </span>
                            </div>
                            <v-progress-linear
                              :value="itemPercent(item)"
                              height="6"
                              rounded
                              color="#1976d2"
                              background-color="#f5f8fc"
                            />
                          </div>
                        </td>
                        <td class="file-process-col-count text-no-wrap">
                          {{ itemProcessed(item) }} / {{ itemTotalRows(item) }}
                          {{ $t("row") }}
                        </td>
                        <td class="file-process-col-user text-no-wrap text-truncate">
                          {{ itemUserName(item) || "—" }}
                        </td>
                        <td class="file-process-col-date text-no-wrap">
                          {{ itemCreatedAt(item) }}
                        </td>
                        <td class="file-process-col-status" @click.stop>
                          <v-chip
                            v-if="itemStatus(item)"
                            x-small
                            label
                            outlined
                            :color="statusColor(itemStatus(item))"
                          >
                            {{ statusLabel(itemStatus(item)) }}
                          </v-chip>
                          <span v-else class="file-process-no-error">{{ $t("haveNot") }}</span>
                        </td>
                        <td class="file-process-col-errors" @click.stop>
                          <v-chip
                            v-if="hasErrors(item)"
                            x-small
                            label
                            outlined
                            color="error"
                            class="file-process-error-chip"
                            @click="openErrorsDialog(item)"
                          >
                            {{ errorItems(item).length }}
                          </v-chip>
                          <span v-else class="file-process-no-error">{{ $t("haveNot") }}</span>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>

              <div class="file-process-pagination d-flex align-center flex-wrap">
                <div class="file-process-per-page d-flex align-center">
                  <span class="file-process-list-count ml-2">
                    {{ $t("item-per-page") }}
                  </span>
                  <v-select
                    v-model="perPage"
                    :items="perPageOptions"
                    dense
                    hide-details
                    solo
                    flat
                    class="file-process-per-page-select"
                  />
                </div>

                <span class="file-process-list-count">
                  {{ pageFrom }} {{ $t("until") }} {{ pageTo }}
                  {{ $t("of") }} {{ filteredItems.length }}
                </span>

                <div class="file-process-page-nav d-flex align-center">
                  <v-btn
                    icon
                    x-small
                    :disabled="page <= 1"
                    @click="goFirstPage"
                  >
                    <v-icon small>mdi-arrow-collapse-right</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="page <= 1"
                    @click="goPrevPage"
                  >
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="page >= pageCount"
                    @click="goNextPage"
                  >
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    x-small
                    :disabled="page >= pageCount"
                    @click="goLastPage"
                  >
                    <v-icon small>mdi-arrow-collapse-left</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="errorsDialog"
      max-width="620"
      @click:outside="closeErrorsDialog"
    >
      <v-card v-if="errorsDialogItem" class="file-process-errors-card">
        <div class="file-process-errors-head d-flex align-center justify-space-between">
          <div class="file-process-errors-head-text">
            <div class="file-process-errors-title">
              {{ $t("import-error-details") }}
            </div>
            <div class="file-process-errors-subtitle text-truncate">
              <template v-if="errorsDialogFileTitle">
                {{ errorsDialogFileTitle }}
                —
                {{ errorsDialogTotal }}
                {{ $t("import-file-errors-count") }}
              </template>
              <template v-else>
                {{ errorsDialogTotal }}
                {{ $t("import-file-errors-count") }}
              </template>
            </div>
          </div>
          <v-btn icon small color="grey darken-2" @click="closeErrorsDialog">
            <v-icon class="close-hover" small>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="file-process-errors-body pa-0">
          <div
            v-if="!errorsDialogAll.length"
            class="file-process-errors-empty text-center"
          >
            {{ $t("no-data-found") }}
          </div>
          <v-simple-table v-else dense class="file-process-errors-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="file-process-errors-col-index">#</th>
                  <th class="file-process-errors-col-row">
                    {{ $t("row") }} {{ $t("excel") }}
                  </th>
                  <th class="file-process-errors-col-field">
                    {{ $t("column") }}
                  </th>
                  <th class="file-process-errors-col-msg">
                    {{ $t("description") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(err, index) in errorsDialogPaged"
                  :key="'err-' + errorsDialogFrom + index"
                >
                  <td class="file-process-errors-col-index">
                    {{ errorsDialogFrom + index }}
                  </td>
                  <td class="file-process-errors-col-row">
                    <span class="file-process-errors-row-badge">
                      {{ errorRowLabel(err) }}
                    </span>
                  </td>
                  <td class="file-process-errors-col-field">
                    {{ errorFieldLabel(err) }}
                  </td>
                  <td class="file-process-errors-col-msg">
                    {{ errorMessageLabel(err) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>

        <div
          v-if="errorsDialogAll.length"
          class="file-process-pagination file-process-errors-pagination d-flex align-center flex-wrap"
        >
          <div class="file-process-per-page d-flex align-center">
            <span class="file-process-list-count ml-2">
              {{ $t("item-per-page") }}
            </span>
            <v-select
              v-model="errorsPerPage"
              :items="perPageOptions"
              dense
              hide-details
              solo
              flat
              class="file-process-per-page-select"
            />
          </div>

          <span class="file-process-list-count">
            {{ errorsDialogFrom }} {{ $t("until") }} {{ errorsDialogTo }}
            {{ $t("of") }} {{ errorsDialogTotal }}
          </span>

          <div class="file-process-page-nav d-flex align-center">
            <v-btn
              icon
              x-small
              :disabled="errorsPage <= 1"
              @click="errorsPage = 1"
            >
              <v-icon small>mdi-arrow-collapse-right</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="errorsPage <= 1"
              @click="errorsPage = Math.max(1, errorsPage - 1)"
            >
              <v-icon small>mdi-minus</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="errorsPage >= errorsDialogPageCount"
              @click="errorsPage = Math.min(errorsDialogPageCount, errorsPage + 1)"
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
            <v-btn
              icon
              x-small
              :disabled="errorsPage >= errorsDialogPageCount"
              @click="errorsPage = errorsDialogPageCount"
            >
              <v-icon small>mdi-arrow-collapse-left</v-icon>
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="uploadDialog"
      max-width="420"
      @click:outside="onUploadClickOutside"
    >
      <v-card class="file-process-upload-card pa-4">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="file-process-upload-title">
            {{ $t("uploadFile") }}
          </div>
          <v-btn icon small @click="closeUploadDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="file-process-sample-combo mb-3">
          <v-select
            v-model="importType"
            :items="uploadTypeItems"
            dense
            hide-details
            solo
            flat
            :disabled="uploading"
            :menu-props="{ offsetY: true, nudgeBottom: 2 }"
            class="file-process-upload-type-select"
            @change="onUploadTypeChange"
          />
          <a
            v-if="currentTypeConfig && currentTypeConfig.sample"
            :href="currentTypeConfig.sample"
            download
            class="file-process-sample-btn"
            :title="$t('sampleFileOf', { name: $t(importType) })"
          >
            <v-icon size="18">mdi-download</v-icon>
            <span class="file-process-sample-btn-text">
              {{ $t("sampleFileOf", { name: $t(importType) }) }}
            </span>
          </a>
        </div>

        <div v-if="needsDigikalaPanel" class="file-process-digikala-wrap">
          <autocomplete
            v-model="digikalaPanel"
            class="file-process-digikala-field"
            :rules="[(v) => !!v || $t('is-required')]"
            :title="$t('digikalaPanelsName')"
            type="digikalaPanels"
          />
        </div>

        <div
          class="file-process-dropzone"
          :class="{ 'is-filled': !!selectedFile }"
          @click="handleFileImport"
        >
          <template v-if="selectedFile">
            <v-icon color="primary" class="mb-1">mdi-file-excel-outline</v-icon>
            <div class="file-process-dropzone-name text-truncate">
              {{ selectedFile.name }}
            </div>
            <div class="file-process-dropzone-hint">
              {{ $t("uploadFile") }}
            </div>
          </template>
          <template v-else>
            <v-icon color="grey" class="mb-1">mdi-cloud-upload-outline</v-icon>
            <div class="file-process-dropzone-hint">
              {{ $t("uploadFile") }}
            </div>
          </template>
        </div>

        <div class="file-process-upload-actions d-flex align-center mt-4">
          <v-btn
            color="error"
            outlined
            dense
            height="34"
            :disabled="uploading"
            @click="closeUploadDialog"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            color="primary"
            dense
            height="34"
            min-width="88"
            :loading="uploading"
            :disabled="!canSubmitUpload"
            @click="submitUpload"
          >
            {{ $t("submit") }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor"
    />
  </v-container>
</template>

<script>
import Snakbar from "@/views/dashboard/components/general/snakbar/snakbar.vue";
import MyIcon from "@/views/dashboard/components/general/icon/MyIcon.vue";
import Autocomplete from "@/views/dashboard/components/general/patial/autocomplete.vue";
import General from "@/Mixins/general";
import { subscribeImportProgress } from "@/realtime";

export default {
  name: "UserFileProcess",
  components: {
    Snakbar,
    MyIcon,
    Autocomplete,
  },
  mixins: [General],
  props: {
    hideActivator: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialog: false,
      importType: "product",
      filterType: "all",
      digikalaPanel: null,
      // value = type فرم API (نه اسلاگ کانال). channel از پاسخ سرور خوانده می‌شود.
      importTypes: [
        { value: "product", sample: "/sample/product.xlsx", enabled: true },
        { value: "customer", sample: "/sample/customer.xlsx", enabled: true },
        { value: "invoice", sample: "/sample/invoice.xlsx", enabled: true },
        {
          value: "accountingDocument",
          sample: "/sample/accountingDocument.xlsx",
          enabled: true,
        },
        { value: "updateFixedPrice", sample: null, enabled: true },
        {
          value: "productIntegrator",
          sample: "/sample/integrator.xlsx",
          enabled: true,
        },
        { value: "receivePay", sample: "/sample/pay.xlsx", enabled: true },
        { value: "recieve", sample: "/sample/pay.xlsx", enabled: true },
        { value: "pay", sample: "/sample/pay.xlsx", enabled: true },
        {
          value: "translationPrice",
          sample: "/sample/translation.xlsx",
          enabled: true,
        },
        {
          value: "accountStructure",
          sample: "/sample/accountStructure.xlsx",
          enabled: true,
        },
        { value: "entrepot", sample: "/sample/entrepot.xlsx", enabled: true },
        {
          value: "warehouseHandling",
          sample: "/sample/warehouseHandling.xlsx",
          enabled: true,
        },
        {
          value: "digikalaOrder",
          sample: null,
          enabled: true,
          needsRefType: true,
        },
        { value: "pricing", sample: "/sample/pricing.xlsx", enabled: true },
        { value: "production", sample: "/sample/production.xlsx", enabled: true },
        {
          value: "receiveChecks",
          sample: "/sample/recieveCheckSample.xlsx",
          enabled: true,
        },
        { value: "barcode", sample: "/sample/barcode.xlsx", enabled: true },
      ],
      items: [],
      page: 1,
      perPage: 10,
      perPageOptions: [10, 25, 50, 100, 500],
      listTake: 500,
      loadingList: false,
      selectedFile: null,
      lastAttachedName: null,
      searchQuery: "",
      searchFocused: false,
      searchExpanded: false,
      uploadDialog: false,
      errorsDialog: false,
      errorsDialogItem: null,
      errorsPage: 1,
      errorsPerPage: 10,
      isSelecting: false,
      uploading: false,
      cancelling: false,
      selectedIds: [],
      activeImportId: null,
      activeProgress: null,
      importWatcher: null,
      snakbar: false,
      snakbarText: null,
      snakbarColor: "pink",
    };
  },
  computed: {
    title() {
      return [this.$t("progress"), this.$t("processing"), this.$t("file")].join(" ");
    },
    visibleImportTypes() {
      return this.importTypes.filter(function (item) {
        return item.enabled;
      });
    },
    currentTypeConfig() {
      var app = this;
      return (
        this.importTypes.find(function (item) {
          return item.value === app.importType;
        }) || null
      );
    },
    uploadTypeItems() {
      var app = this;
      return this.visibleImportTypes.map(function (item) {
        return {
          value: item.value,
          text: app.$t(item.value),
        };
      });
    },
    needsDigikalaPanel() {
      return !!(this.currentTypeConfig && this.currentTypeConfig.needsRefType);
    },
    canSubmitUpload() {
      if (!this.selectedFile || this.uploading) {
        return false;
      }
      if (this.needsDigikalaPanel && !this.digikalaPanel) {
        return false;
      }
      return true;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.perPage) || 1);
    },
    pageFrom() {
      if (!this.filteredItems.length) {
        return 0;
      }
      return (this.page - 1) * this.perPage + 1;
    },
    pageTo() {
      return Math.min(this.page * this.perPage, this.filteredItems.length);
    },
    filteredItems() {
      var app = this;
      var q = String(this.searchQuery || "")
        .trim()
        .toLowerCase();
      if (!q) {
        return this.items;
      }
      return this.items.filter(function (item) {
        return (
          String(app.itemFileTitle(item))
            .toLowerCase()
            .indexOf(q) !== -1
        );
      });
    },
    pagedItems() {
      var start = (this.page - 1) * this.perPage;
      return this.filteredItems.slice(start, start + this.perPage);
    },
    hasActiveImport() {
      var app = this;
      return this.items.some(function (item) {
        return !app.isFinished(item);
      });
    },
    allSelected() {
      var app = this;
      return (
        this.filteredItems.length > 0 &&
        this.filteredItems.every(function (item) {
          return app.selectedIds.indexOf(item.id) !== -1;
        })
      );
    },
    someSelected() {
      var app = this;
      if (!this.filteredItems.length || this.allSelected) {
        return false;
      }
      return this.filteredItems.some(function (item) {
        return app.selectedIds.indexOf(item.id) !== -1;
      });
    },
    listSummary() {
      var app = this;
      var total = this.items.length;
      var success = 0;
      var pending = 0;
      var failed = 0;
      var cancelled = 0;
      this.items.forEach(function (item) {
        var status = String(app.itemStatus(item)).toLowerCase();
        if (status === "done" || status === "completed") {
          success += 1;
        } else if (status === "failed" || status === "error" || status === "unsuccessful") {
          failed += 1;
        } else if (status === "cancelled") {
          cancelled += 1;
        } else {
          pending += 1;
        }
      });
      return {
        total: total,
        success: success,
        pending: pending,
        failed: failed,
        cancelled: cancelled,
        percent: total ? Math.floor((success / total) * 100) : 0,
      };
    },
    errorsDialogFileTitle() {
      return this.itemFileTitle(this.errorsDialogItem);
    },
    errorsDialogAll() {
      if (!this.errorsDialogItem) {
        return [];
      }
      return this.errorItems(this.errorsDialogItem);
    },
    errorsDialogTotal() {
      return this.errorsDialogAll.length;
    },
    errorsDialogPageCount() {
      return Math.max(
        1,
        Math.ceil(this.errorsDialogAll.length / this.errorsPerPage) || 1
      );
    },
    errorsDialogFrom() {
      if (!this.errorsDialogAll.length) {
        return 0;
      }
      return (this.errorsPage - 1) * this.errorsPerPage + 1;
    },
    errorsDialogTo() {
      return Math.min(
        this.errorsPage * this.errorsPerPage,
        this.errorsDialogAll.length
      );
    },
    errorsDialogPaged() {
      var start = (this.errorsPage - 1) * this.errorsPerPage;
      return this.errorsDialogAll.slice(start, start + this.errorsPerPage);
    },
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.teardownSocket();
        this.selectedIds = [];
        this.searchQuery = "";
        this.searchExpanded = false;
        this.searchFocused = false;
        this.uploadDialog = false;
        this.closeErrorsDialog();
        this.clearSelectedFile();
      }
    },
    errorsDialogAll() {
      if (this.errorsPage > this.errorsDialogPageCount) {
        this.errorsPage = this.errorsDialogPageCount;
      }
    },
    errorsPerPage() {
      this.errorsPage = 1;
    },
    filterType() {
      if (this.dialog) {
        this.selectedFile = null;
        this.activeImportId = null;
        this.activeProgress = null;
        this.selectedIds = [];
        this.lastAttachedName = null;
        this.uploadDialog = false;
        this.searchQuery = "";
        this.searchExpanded = false;
        this.searchFocused = false;
        this.page = 1;
        this.fetchList();
      }
    },
    items() {
      if (this.page > this.pageCount) {
        this.page = this.pageCount;
      }
      this.pruneSelectedIds();
    },
    searchQuery() {
      this.page = 1;
    },
    perPage() {
      this.page = 1;
    },
  },
  created() {
    if (this.$root) {
      this.$root.$on("open-user-file-process", this.openFromOutside);
    }
  },
  beforeDestroy() {
    if (this.$root) {
      this.$root.$off("open-user-file-process", this.openFromOutside);
    }
    this.teardownSocket();
  },
  methods: {
    goFirstPage() {
      this.page = 1;
    },
    goPrevPage() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    goNextPage() {
      if (this.page < this.pageCount) {
        this.page += 1;
      }
    },
    goLastPage() {
      this.page = this.pageCount;
    },
    openDialog() {
      this.dialog = true;
      this.connectSocket();
      this.fetchList();
    },
    /**
     * Open progress dialog from list pages via `$root.$emit("open-user-file-process", { type })`.
     * Keeps existing activator/openDialog behavior unchanged.
     */
    openFromOutside(payload) {
      var type = null;
      if (payload && typeof payload === "object") {
        type = payload.type || null;
      } else if (typeof payload === "string") {
        type = payload;
      }
      if (type) {
        var typeItem = this.importTypes.find(function (item) {
          return item.value === type;
        });
        if (typeItem && typeItem.enabled) {
          if (this.dialog) {
            this.selectFilterType(type);
            return;
          }
          this.importType = type;
          this.filterType = type;
        }
      }
      this.openDialog();
    },
    closeDialog() {
      this.dialog = false;
      this.selectedFile = null;
      this.teardownSocket();
    },
    selectFilterType(value) {
      if (this.uploading || !value) {
        return;
      }
      if (value !== "all") {
        var typeItem = this.importTypes.find(function (item) {
          return item.value === value;
        });
        if (!typeItem || !typeItem.enabled) {
          return;
        }
        this.importType = value;
      }
      if (this.filterType === value) {
        return;
      }
      this.filterType = value;
    },
    selectImportType(item) {
      if (!item || !item.enabled || this.uploading) {
        return;
      }
      this.selectFilterType(item.value);
    },
    getRealtime() {
      if (!this.$root) {
        return null;
      }
      return this.$root.realtime || this.$root.chatRealtime || null;
    },
    onRealtimeReady() {
      if (!this.dialog) {
        return;
      }
      this.connectSocket();
    },
    connectSocket() {
      var app = this;
      this.teardownSocket();
      var realtime = this.getRealtime();
      if (!realtime || typeof realtime.subscribe !== "function") {
        if (this.$root) {
          this.$root.$on("realtime-ready", this.onRealtimeReady);
          this.$root.$on("chat-realtime-ready", this.onRealtimeReady);
        }
        return;
      }
      this.importWatcher = subscribeImportProgress(realtime, function (payload) {
        app.onProgress(payload);
      });
      this.syncImportChannels();
    },
    teardownSocket() {
      if (this.$root) {
        this.$root.$off("realtime-ready", this.onRealtimeReady);
        this.$root.$off("chat-realtime-ready", this.onRealtimeReady);
      }
      if (this.importWatcher && typeof this.importWatcher.destroy === "function") {
        this.importWatcher.destroy();
      }
      this.importWatcher = null;
    },
    itemChannel(item) {
      if (!item) {
        return null;
      }
      if (item.channel) {
        return String(item.channel);
      }
      if (item.progress && item.progress.channel) {
        return String(item.progress.channel);
      }
      return null;
    },
    itemImportId(item) {
      if (!item) {
        return null;
      }
      if (item.id != null && String(item.id).indexOf("local-") !== 0) {
        return item.id;
      }
      if (item.progress && item.progress.import_id != null) {
        return item.progress.import_id;
      }
      if (item.import_id != null) {
        return item.import_id;
      }
      return item.id != null ? item.id : null;
    },
    syncImportChannels() {
      if (!this.importWatcher || typeof this.importWatcher.setChannels !== "function") {
        return;
      }
      var app = this;
      var channels = [];
      this.items.forEach(function (item) {
        if (app.isFinished(item)) {
          return;
        }
        var channel = app.itemChannel(item);
        if (channel && channels.indexOf(channel) === -1) {
          channels.push(channel);
        }
      });
      this.importWatcher.setChannels(channels);
    },
    fetchList() {
      var app = this;
      app.loadingList = true;
      var params = {
        take: app.listTake,
        type: app.filterType || "all",
      };
      app.$axios
        .get(app.$endpointI + "import", {
          params: params,
        })
        .then(function (res) {
          var list = Array.isArray(res.data)
            ? res.data
            : res.data && Array.isArray(res.data.data)
              ? res.data.data
              : [];
          app.items = list;
          app.page = 1;
          app.syncActiveFromList();
          app.syncImportChannels();
        })
        .catch(function (err) {
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loadingList = false;
        });
    },
    syncActiveFromList() {
      var active = null;
      var activeImportId = null;
      for (var i = 0; i < this.items.length; i++) {
        var item = this.items[i];
        if (this.isFinished(item)) {
          continue;
        }
        var progress = item.progress || null;
        if (progress) {
          active = progress;
          activeImportId = this.itemImportId(item);
          break;
        }
        activeImportId = this.itemImportId(item);
        break;
      }
      this.activeImportId = activeImportId;
      this.activeProgress = active;
    },
    expandSearch() {
      var app = this;
      this.searchExpanded = true;
      this.$nextTick(function () {
        if (app.$refs.searchInput) {
          app.$refs.searchInput.focus();
        }
      });
    },
    onSearchBlur() {
      this.searchFocused = false;
      if (!this.searchQuery) {
        this.searchExpanded = false;
      }
    },
    clearSearch() {
      this.searchQuery = "";
      this.searchExpanded = false;
      this.searchFocused = false;
    },
    clearAttachedFile() {
      this.lastAttachedName = null;
    },
    openUploadDialog() {
      this.digikalaPanel = null;
      if (this.filterType && this.filterType !== "all") {
        this.importType = this.filterType;
      }
      this.uploadDialog = true;
    },
    closeUploadDialog() {
      if (this.uploading) {
        return;
      }
      this.uploadDialog = false;
      this.digikalaPanel = null;
      this.clearSelectedFile();
      this.syncFilterFromUploadType();
    },
    onUploadClickOutside(e) {
      if (this.isUploadSelectMenuEvent(e)) {
        return;
      }
      this.closeUploadDialog();
    },
    isUploadSelectMenuEvent(e) {
      if (!e) {
        return false;
      }
      var path =
        typeof e.composedPath === "function"
          ? e.composedPath()
          : e.path || [];
      var i;
      for (i = 0; i < path.length; i++) {
        var el = path[i];
        if (!el || !el.classList) {
          continue;
        }
        if (
          el.classList.contains("v-menu__content") ||
          el.classList.contains("v-select-list") ||
          el.classList.contains("v-list")
        ) {
          return true;
        }
      }
      var target = e.target;
      if (target && typeof target.closest === "function") {
        return !!(
          target.closest(".v-menu__content") ||
          target.closest(".v-select-list")
        );
      }
      return false;
    },
    onUploadTypeChange() {
      this.digikalaPanel = null;
      this.clearSelectedFile();
      this.lastAttachedName = null;
    },
    syncFilterFromUploadType() {
      if (
        this.filterType &&
        this.filterType !== "all" &&
        this.filterType !== this.importType
      ) {
        this.filterType = this.importType;
      }
    },
    submitUpload() {
      var app = this;
      if (!this.selectedFile || this.uploading) {
        return;
      }
      var attachedName = this.selectedFile.name;
      this.uploadFile(function () {
        app.lastAttachedName = attachedName;
        app.uploadDialog = false;
        app.syncFilterFromUploadType();
      });
    },
    handleFileImport() {
      var app = this;
      this.isSelecting = true;
      window.addEventListener(
        "focus",
        function () {
          app.isSelecting = false;
        },
        { once: true }
      );
      this.$refs.uploader.click();
    },
    onFileChanged(e) {
      this.selectedFile = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    },
    clearSelectedFile() {
      this.selectedFile = null;
      if (this.$refs.uploader) {
        this.$refs.uploader.value = "";
      }
    },
    uploadFile(onSuccess) {
      var app = this;
      if (!this.canSubmitUpload) {
        return;
      }
      if (!this.importWatcher) {
        this.connectSocket();
      }
      var formData = new FormData();
      formData.append("selectedFile", this.selectedFile);
      formData.append("type", this.importType);
      if (this.needsDigikalaPanel && this.digikalaPanel) {
        formData.append(
          "ref_type",
          this.digikalaPanel.name || this.digikalaPanel.title || ""
        );
      }
      this.uploading = true;
      this.$axios
        .post(this.$endpointI + "import", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then(function (res) {
          var data = res.data || {};
          var file = data.data || {};
          var importId =
            data.import_id != null
              ? data.import_id
              : data.progress && data.progress.import_id != null
                ? data.progress.import_id
                : null;
          var channel =
            data.channel ||
            (data.progress && data.progress.channel) ||
            null;
          var progress = data.progress || {
            import_id: importId,
            file_id: file.id,
            channel: channel,
            status: "queue",
            finished: false,
            percent: 0,
            total: 0,
            processed: 0,
            succeeded: 0,
            failed: 0,
            errors: null,
          };
          if (progress.import_id == null && importId != null) {
            progress.import_id = importId;
          }
          if (!progress.channel && channel) {
            progress.channel = channel;
          }
          app.activeImportId = importId;
          app.activeProgress = progress;
          app.selectedFile = null;
          app.digikalaPanel = null;
          if (app.$refs.uploader) {
            app.$refs.uploader.value = "";
          }
          app.page = 1;
          app.prependListItem({
            id: importId,
            file_id: file.id != null ? file.id : progress.file_id,
            type: app.importType,
            Status: progress.status,
            status: progress.status,
            queue: data.queue || null,
            channel: channel,
            count_item: progress.succeeded || 0,
            file: file,
            user: null,
            progress: progress,
          });
          if (channel && app.importWatcher && typeof app.importWatcher.add === "function") {
            app.importWatcher.add(channel);
          }
          // اعتبارسنجی رد شده از همان پاسخ اولیه
          if (progress.finished === true) {
            app.onProgress(progress);
          }
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        })
        .catch(function (err) {
          app.serverErrorHandeler(err);
          // بعضی خطاهای پیام‌دار خارج از 420
          var res = err && err.response;
          if (
            res &&
            res.data &&
            res.data.message &&
            res.status !== 420 &&
            res.status !== 422 &&
            res.status !== 401
          ) {
            app.snakbarText = res.data.message;
            app.snakbarColor = "pink";
            app.snakbar = true;
          }
        })
        .finally(function () {
          app.uploading = false;
        });
    },
    cancelImport() {
      var app = this;
      if (this.cancelling || !this.selectedIds.length) {
        return;
      }
      this.cancelling = true;
      var ids = this.selectedIds.slice();
      this.$axios
        .post(this.$endpointI + "import/cancel", { ids: ids })
        .then(function (res) {
          var files =
            res.data && Array.isArray(res.data.files) ? res.data.files : [];
          files.forEach(function (file) {
            var progress = file && file.progress ? file.progress : file;
            if (progress) {
              app.onProgress(progress);
            }
          });
          app.selectedIds = [];
        })
        .catch(function (err) {
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.cancelling = false;
        });
    },
    isRowSelected(item) {
      return item && this.selectedIds.indexOf(item.id) !== -1;
    },
    toggleRow(item) {
      if (!item || item.id == null) {
        return;
      }
      var idx = this.selectedIds.indexOf(item.id);
      if (idx === -1) {
        this.selectedIds = this.selectedIds.concat([item.id]);
      } else {
        this.selectedIds = this.selectedIds.filter(function (id) {
          return id !== item.id;
        });
      }
    },
    toggleSelectAll() {
      if (this.allSelected) {
        var filteredMap = {};
        this.filteredItems.forEach(function (item) {
          if (item && item.id != null) {
            filteredMap[item.id] = true;
          }
        });
        this.selectedIds = this.selectedIds.filter(function (id) {
          return !filteredMap[id];
        });
        return;
      }
      var next = this.selectedIds.slice();
      this.filteredItems.forEach(function (item) {
        if (item && item.id != null && next.indexOf(item.id) === -1) {
          next.push(item.id);
        }
      });
      this.selectedIds = next;
    },
    pruneSelectedIds() {
      var valid = {};
      this.items.forEach(function (item) {
        if (item && item.id != null) {
          valid[item.id] = true;
        }
      });
      this.selectedIds = this.selectedIds.filter(function (id) {
        return valid[id];
      });
    },
    onProgress(payload) {
      if (!payload) {
        return;
      }
      if (payload.import_id == null && payload.file_id == null) {
        return;
      }
      this.patchListItem(payload);
      if (payload.finished === true) {
        if (
          payload.import_id != null &&
          Number(this.activeImportId) === Number(payload.import_id)
        ) {
          this.activeImportId = null;
          this.activeProgress = null;
        }
        if (
          payload.channel &&
          this.importWatcher &&
          typeof this.importWatcher.remove === "function"
        ) {
          this.importWatcher.remove(payload.channel);
        }
        this.syncActiveFromList();
        this.syncImportChannels();
        return;
      }
      if (payload.import_id != null) {
        this.activeImportId = payload.import_id;
      }
      this.activeProgress = Object.assign({}, payload);
    },
    prependListItem(item) {
      var importId = this.itemImportId(item);
      var fileId = item.file_id;
      var exists = this.items.some(function (row) {
        if (importId != null && row.id != null && Number(row.id) === Number(importId)) {
          return true;
        }
        return (
          fileId != null &&
          (Number(row.file_id) === Number(fileId) ||
            (row.file && Number(row.file.id) === Number(fileId)))
        );
      });
      if (exists) {
        this.patchListItem(item.progress || item);
        return;
      }
      this.items = [item].concat(this.items);
    },
    patchListItem(progress) {
      var importId = progress.import_id;
      var fileId = progress.file_id;
      var found = false;
      this.items = this.items.map(function (item) {
        var itemImportId =
          item.id != null && String(item.id).indexOf("local-") !== 0
            ? item.id
            : item.progress && item.progress.import_id != null
              ? item.progress.import_id
              : item.import_id;
        var matched =
          importId != null &&
          itemImportId != null &&
          Number(itemImportId) === Number(importId);
        if (!matched && fileId != null) {
          var itemFileId =
            item.file_id != null
              ? item.file_id
              : item.file && item.file.id != null
                ? item.file.id
                : null;
          matched = itemFileId != null && Number(itemFileId) === Number(fileId);
        }
        if (!matched) {
          return item;
        }
        found = true;
        return Object.assign({}, item, {
          id: importId != null ? importId : item.id,
          file_id: fileId != null ? fileId : item.file_id,
          channel: progress.channel || item.channel,
          Status: progress.status,
          status: progress.status,
          count_item:
            progress.succeeded != null ? progress.succeeded : item.count_item,
          progress: Object.assign({}, item.progress || {}, progress),
        });
      });
      if (!found && progress.status) {
        this.items = [
          {
            id: importId != null ? importId : "socket-" + fileId,
            file_id: fileId,
            type: this.importType,
            channel: progress.channel || null,
            Status: progress.status,
            status: progress.status,
            count_item: progress.succeeded || 0,
            file: { id: fileId, title: fileId != null ? "#" + fileId : "-" },
            progress: progress,
          },
        ].concat(this.items);
      }
    },
    itemProgress(item) {
      return item && item.progress ? item.progress : null;
    },
    itemStatus(item) {
      var status = "";
      if (item && item.progress && item.progress.status) {
        status = item.progress.status;
      } else if (item) {
        status = item.Status || item.status || "";
      }
      status = String(status || "").trim();
      if (!status || status === "null" || status === "undefined") {
        return "";
      }
      return status;
    },
    itemFileTitle(item) {
      if (item && item.file && item.file.title) {
        return item.file.title;
      }
      return item && item.file_id ? "#" + item.file_id : "-";
    },
    itemUserName(item) {
      if (!item || !item.user) {
        return "";
      }
      return (
        (item.user.firstname || "") + " " + (item.user.lastname || "")
      ).trim();
    },
    itemCreatedAt(item) {
      var raw =
        item && item.created_at != null
          ? item.created_at
          : item && item.createdAt != null
            ? item.createdAt
            : null;
      if (raw == null || raw === "") {
        return "—";
      }
      try {
        if (this.$moment) {
          return this.$moment(raw).format("jYYYY/jMM/jDD HH:mm");
        }
      } catch (e) {}
      return String(raw);
    },
    itemProcessed(item) {
      var progress = this.itemProgress(item);
      if (progress && progress.processed != null) {
        return Number(progress.processed) || 0;
      }
      return Number(item && item.count_item) || 0;
    },
    itemTotalRows(item) {
      var progress = this.itemProgress(item);
      if (progress && progress.total) {
        return Number(progress.total) || 0;
      }
      return Number(item && item.count_item) || 0;
    },
    itemPercent(item) {
      var progress = this.itemProgress(item);
      if (progress && progress.percent != null) {
        return Number(progress.percent) || 0;
      }
      var status = String(this.itemStatus(item)).toLowerCase();
      if (status === "done" || status === "completed") {
        return 100;
      }
      var total = this.itemTotalRows(item);
      var processed = this.itemProcessed(item);
      if (total > 0) {
        return Math.floor((processed / total) * 100);
      }
      return 0;
    },
    isFinished(item) {
      var progress = this.itemProgress(item);
      if (progress && progress.finished === true) {
        return true;
      }
      var status = String(this.itemStatus(item)).toLowerCase();
      return ["done", "completed", "failed", "cancelled", "error"].indexOf(status) !== -1;
    },
    isFailed(status) {
      return ["failed", "error", "unsuccessful"].indexOf(String(status).toLowerCase()) !== -1;
    },
    statusColor(status) {
      var map = {
        queue: "grey",
        validating: "orange",
        validate: "orange",
        saving: "primary",
        done: "green",
        completed: "green",
        failed: "red",
        error: "red",
        cancelled: "blue-grey",
      };
      return map[String(status).toLowerCase()] || "grey";
    },
    statusLabel(status) {
      var key = String(status || "").toLowerCase().trim();
      if (!key) {
        return this.$t("haveNot");
      }
      if (key === "done") {
        return this.$t("completed");
      }
      if (key === "queue") {
        return this.$t("queue");
      }
      if (key === "saving") {
        return this.$t("saving");
      }
      if (this.$te(key)) {
        return this.$t(key);
      }
      return status;
    },
    progressLabel(progress) {
      if (!progress) {
        return this.$t("queue");
      }
      var status = String(progress.status || "").toLowerCase();
      if (status === "validating" || status === "validate") {
        return this.$t("validating") + "...";
      }
      if (status === "cancelled") {
        return (
          this.$t("cancelled") +
          " — " +
          (progress.processed || 0) +
          " / " +
          (progress.total || 0)
        );
      }
      if (status === "saving" && Number(progress.processed) === 0) {
        return (
          this.$t("processing_") +
          " " +
          (progress.total || 0) +
          " " +
          this.$t("row")
        );
      }
      if (progress.total) {
        return (
          (progress.percent || 0) +
          "% — " +
          (progress.processed || 0) +
          " / " +
          progress.total
        );
      }
      return this.$t("queue");
    },
    hasErrors(item) {
      if (!item) {
        return false;
      }
      var errors =
        (item.progress && item.progress.errors) || item.errors || null;
      if (!errors) {
        return false;
      }
      if (Array.isArray(errors.items) && errors.items.length) {
        return true;
      }
      if (Array.isArray(errors) && errors.length) {
        return true;
      }
      return false;
    },
    errorItems(item) {
      if (!item) {
        return [];
      }
      var errors =
        (item.progress && item.progress.errors) || item.errors || null;
      if (!errors) {
        return [];
      }
      if (Array.isArray(errors.items)) {
        return errors.items;
      }
      if (Array.isArray(errors)) {
        return errors;
      }
      return [];
    },
    openErrorsDialog(item) {
      if (!item || !this.hasErrors(item)) {
        return;
      }
      this.errorsDialogItem = item;
      this.errorsPage = 1;
      this.errorsDialog = true;
    },
    closeErrorsDialog() {
      this.errorsDialog = false;
      this.errorsDialogItem = null;
      this.errorsPage = 1;
    },
    errorRowLabel(err) {
      if (!err) {
        return "—";
      }
      if (err.row != null && err.row !== "") {
        return err.row;
      }
      if (err.line != null && err.line !== "") {
        return err.line;
      }
      if (err.row_number != null && err.row_number !== "") {
        return err.row_number;
      }
      return "—";
    },
    errorFieldLabel(err) {
      if (!err) {
        return "—";
      }
      var value =
        err.column != null
          ? err.column
          : err.field != null
            ? err.field
            : err.attribute != null
              ? err.attribute
              : err.key != null
                ? err.key
                : null;
      if (value == null || value === "") {
        return "—";
      }
      return value;
    },
    errorMessageLabel(err) {
      if (!err) {
        return "—";
      }
      if (err.message != null && err.message !== "") {
        return err.message;
      }
      if (err.error != null && err.error !== "") {
        return err.error;
      }
      if (typeof err === "string") {
        return err;
      }
      return "—";
    },
  },
};
</script>

<style scoped>
.file-process-card {
  border-radius: 4px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 64px);
}
.file-process-header {
  padding: 10px 12px;
  flex-shrink: 0;
}
.file-process-title {
  font-size: 14px;
  font-weight: 600;
}
.file-process-body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.file-process-types {
  gap: 6px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.file-process-type-chip {
  cursor: pointer;
}
.file-process-toolbar {
  gap: 6px;
  margin-bottom: 8px;
  flex-shrink: 0;
  direction: ltr;
  justify-content: flex-end;
}
.file-process-toolbar-actions {
  gap: 6px;
  min-width: 0;
}
.file-process-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  max-width: 140px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.file-process-sort-btn:hover {
  background: #eeeeee;
}
.file-process-sort-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
.file-process-sort-btn.has-type {
  border-color: #90caf9;
  background: #e3f2fd;
}
.file-process-sort-label {
  font-size: 11px;
  color: #607d8b;
  max-width: 80px;
  white-space: nowrap;
}
.file-process-sort-label.is-typed {
  color: #1976d2;
}
.file-process-sort-menu {
  min-width: 180px !important;
  max-width: 220px;
  padding: 4px 0 !important;
}
.file-process-sort-menu ::v-deep .v-list-item {
  min-height: 32px !important;
  padding: 0 10px !important;
}
.file-process-sort-menu ::v-deep .v-list-item__title {
  font-size: 12px !important;
  line-height: 1.3;
  white-space: nowrap;
}
.file-process-sort-menu .is-active {
  background: #e3f2fd;
}
.file-process-search-shell {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  width: 32px;
  padding: 0;
  border-radius: 6px;
  background: #eef0f3;
  border: 1px solid transparent;
  overflow: hidden;
  transition:
    width 0.22s ease,
    padding 0.22s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
.file-process-search-shell.is-expanded {
  width: 220px;
  padding: 0 6px 0 4px;
}
.file-process-search-shell.is-expanded.is-focused {
  background: #fff;
  border-color: #0066ff;
  box-shadow: 0 0 0 2px rgba(0, 102, 255, 0.1);
}
.file-process-search-toggle {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.file-process-search-input {
  flex: 1 1 auto;
  min-width: 0;
  width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  font-family: inherit;
  color: #333;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.file-process-search-shell.is-expanded .file-process-search-input {
  opacity: 1;
  pointer-events: auto;
}
.file-process-search-input::placeholder {
  color: #9aa0a6;
}
.file-process-search-clear {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.file-process-search-clear:hover {
  background: rgba(0, 0, 0, 0.08);
}
.file-process-import-wrap {
  gap: 2px;
  min-width: 0;
  max-width: 220px;
}
.file-process-import-btn {
  position: relative;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid #90caf9;
  background: #e3f2fd;
  color: #1976d2;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.file-process-import-btn:hover {
  background: #bbdefb;
}
.file-process-import-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
.file-process-attached-chip {
  position: relative;
  height: 28px;
  max-width: 100%;
  min-width: 0;
  padding: 0 2px 0 8px;
  border-radius: 6px;
  border: 1px solid #81c784;
  background: #e8f5e9;
  gap: 2px;
}
.file-process-attached-main {
  min-width: 0;
  flex: 1 1 auto;
  max-width: 160px;
  height: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  gap: 4px;
}
.file-process-attached-name {
  font-size: 11px;
  color: #2e7d32;
  min-width: 0;
  line-height: 1;
}
.file-process-import-mark {
  position: absolute;
  top: -4px;
  left: -4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #2e7d32;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1.5px #fff;
  pointer-events: none;
}
.file-process-import-mark .v-icon {
  color: #fff !important;
}
.file-process-import-clear {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 4px;
  background: transparent;
  padding: 0;
  cursor: pointer;
}
.file-process-import-clear:hover {
  background: rgba(46, 125, 50, 0.1);
}
.file-process-upload-title {
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
}
.file-process-upload-actions {
  gap: 8px;
  direction: ltr;
  justify-content: flex-end;
}
.file-process-digikala-wrap {
  margin-bottom: 25px;
}
.file-process-digikala-field ::v-deep .v-input {
  font-size: 12px;
}
.file-process-digikala-field ::v-deep .v-input__control {
  min-height: 32px !important;
}
.file-process-digikala-field ::v-deep .v-input__slot {
  min-height: 32px !important;
  margin-bottom: 0 !important;
}
.file-process-digikala-field ::v-deep .v-text-field__details {
  min-height: 14px;
  margin-bottom: 0 !important;
  padding: 0 4px !important;
}
.file-process-digikala-field ::v-deep .v-messages {
  min-height: 12px;
  font-size: 11px;
}
.file-process-digikala-field ::v-deep .v-label {
  font-size: 12px !important;
}
.file-process-digikala-field ::v-deep .v-input__append-inner {
  margin-top: 4px !important;
}
.file-process-sample-combo {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  background: #f4faf5;
  border: 1px solid #a5d6a7;
}
.file-process-upload-type-select {
  flex: 0 0 128px !important;
  width: 128px !important;
  max-width: 128px !important;
  min-width: 128px !important;
  height: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  font-size: 12px;
  font-weight: 600;
  background: #2e7d32;
  align-self: stretch;
}
.file-process-upload-type-select.v-input {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  padding-top: 0 !important;
}
.file-process-upload-type-select ::v-deep .v-input__control,
.file-process-upload-type-select ::v-deep .v-input__slot,
.file-process-upload-type-select ::v-deep .v-select__slot {
  height: 100% !important;
  min-height: 100% !important;
  max-height: 100% !important;
}
.file-process-upload-type-select ::v-deep .v-input__control {
  width: 100%;
}
.file-process-upload-type-select ::v-deep .v-input__slot {
  padding: 0 6px 0 4px !important;
  box-shadow: none !important;
  background: #2e7d32 !important;
  border-radius: 0 !important;
  margin: 0 !important;
}
.file-process-upload-type-select ::v-deep .v-select__slot {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  width: 100%;
  gap: 2px;
}
.file-process-upload-type-select ::v-deep .v-select__selections {
  flex: 0 1 auto !important;
  display: flex !important;
  align-items: center !important;
  width: auto !important;
  max-width: 92px;
  height: 100%;
  margin: 0 !important;
  padding: 0 !important;
}
.file-process-upload-type-select ::v-deep .v-select__selection {
  font-size: 12px;
  font-weight: 700;
  color: #fff !important;
  margin: 0 !important;
  max-width: 92px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.file-process-upload-type-select ::v-deep .v-input__append-inner {
  margin: 0 0 0 2px !important;
  padding: 0 !important;
  height: 100% !important;
  align-self: center !important;
  display: flex !important;
  align-items: center !important;
}
.file-process-upload-type-select ::v-deep .v-input__icon {
  height: 100% !important;
  min-width: 18px;
}
.file-process-upload-type-select ::v-deep .v-icon {
  color: #fff !important;
  font-size: 16px !important;
}
.file-process-sample-btn {
  flex: 1 1 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  height: 100%;
  padding: 0 12px;
  border: none;
  border-right: 1px solid #c8e6c9;
  background: transparent;
  color: #2e7d32;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}
.file-process-sample-btn:hover {
  background: #e8f5e9;
  color: #1b5e20;
}
.file-process-sample-btn .v-icon {
  color: #2e7d32 !important;
}
.file-process-sample-btn:hover .v-icon {
  color: #1b5e20 !important;
}
.file-process-sample-btn-text {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
@media (max-width: 420px) {
  .file-process-upload-type-select {
    flex-basis: 112px !important;
    width: 112px !important;
    max-width: 112px !important;
    min-width: 112px !important;
  }
  .file-process-sample-btn-text {
    font-size: 11px;
  }
}
.file-process-dropzone {
  border: 1px dashed #c5d0dc;
  border-radius: 8px;
  background: #fafbfc;
  padding: 22px 12px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.file-process-dropzone:hover {
  border-color: #90caf9;
  background: #f7fbff;
}
.file-process-dropzone.is-filled {
  border-style: solid;
  border-color: #90caf9;
  background: #f7fbff;
}
.file-process-dropzone-name {
  font-size: 13px;
  font-weight: 500;
  color: #37474f;
  max-width: 100%;
}
.file-process-dropzone-hint {
  font-size: 12px;
  color: #90a4ae;
  margin-top: 2px;
}
.file-process-active {
  margin-bottom: 8px;
  flex-shrink: 0;
}
.file-process-kpis {
  gap: 6px;
}
.file-process-kpi {
  flex: 1 1 0;
  min-width: 110px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #e5e9f0;
  gap: 6px;
}
.file-process-kpi-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.file-process-kpi-icon--success {
  background: #e8f5e9;
}
.file-process-kpi-icon--queue {
  background: #fff3e0;
}
.file-process-kpi-icon--failed {
  background: #ffebee;
}
.file-process-kpi-icon--cancelled {
  background: #eceff1;
}
.file-process-kpi-label {
  font-size: 11px;
  color: #90a4ae;
  white-space: nowrap;
}
.file-process-kpi-value {
  font-size: 15px;
  font-weight: 700;
  color: #37474f;
  margin-right: auto;
  line-height: 1;
}
.file-process-list-head-actions {
  gap: 8px;
}
.file-process-list-head-actions .v-btn {
  text-transform: none;
  letter-spacing: 0;
}
.file-process-panel {
  margin-top: 4px;
  border: 1px solid #e5e9f0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.file-process-list-head {
  padding: 10px 12px 8px;
  background: #fafbfc;
  border-bottom: 1px solid #e5e9f0;
  flex-shrink: 0;
}
.file-process-list-title {
  margin-bottom: 0;
  font-size: 12px;
  font-weight: 600;
  color: #555;
}
.file-process-list-count {
  font-size: 11px;
  color: #78909c;
  white-space: nowrap;
}
.file-process-panel-body {
  padding: 0 0 12px;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.file-process-pagination {
  margin-top: 0;
  gap: 12px;
  justify-content: space-between;
  border-top: 1px solid #e5e9f0;
  padding: 10px 12px 2px;
  background: transparent;
  flex-shrink: 0;
}
.file-process-per-page {
  min-width: 0;
}
.file-process-per-page-select {
  max-width: 72px;
  font-size: 12px;
}
.file-process-per-page-select ::v-deep .v-input__slot {
  min-height: 28px !important;
  padding: 0 4px !important;
  box-shadow: none !important;
  border-bottom: 1px solid #ddd;
  border-radius: 0 !important;
  background: transparent !important;
}
.file-process-per-page-select ::v-deep .v-select__selection {
  font-size: 12px;
  margin: 0;
}
.file-process-page-nav {
  gap: 2px;
}
.file-process-page-nav .v-btn {
  color: #757575 !important;
}
.file-process-page-nav .v-btn .v-icon {
  color: #757575 !important;
}
.file-process-page-nav .v-btn--disabled {
  opacity: 1 !important;
  color: #bdbdbd !important;
}
.file-process-page-nav .v-btn--disabled .v-icon {
  color: #bdbdbd !important;
}
.file-process-loading {
  min-height: 320px;
}
.file-process-empty {
  font-size: 12px;
  color: #999;
  min-height: 320px;
  padding: 12px;
}
.file-process-list {
  max-height: 320px;
  overflow: hidden;
  padding: 0;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}
.file-process-card--mobile {
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  border-radius: 0 !important;
}
.file-process-card--mobile .file-process-body {
  height: 100%;
}
.file-process-card--mobile .file-process-panel {
  flex: 1 1 auto;
}
.file-process-card--mobile .file-process-list {
  max-height: none;
  flex: 1 1 auto;
}
@media (max-width: 600px) {
  .file-process-card {
    height: 100%;
    border-radius: 0 !important;
  }
  .file-process-body {
    height: 100%;
  }
  .file-process-panel {
    flex: 1 1 auto;
  }
  .file-process-list {
    max-height: none;
    flex: 1 1 auto;
  }
}
.file-process-table {
  background: transparent;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.file-process-table ::v-deep .v-data-table__wrapper {
  overflow: hidden !important;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.file-process-table ::v-deep table {
  table-layout: fixed;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}
.file-process-table ::v-deep thead {
  flex-shrink: 0;
  display: table;
  width: 100%;
  table-layout: fixed;
}
.file-process-table ::v-deep tbody {
  display: block;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  max-height: 276px;
  scrollbar-width: thin;
  scrollbar-color: #d0d5dd transparent;
}
.file-process-card--mobile .file-process-table ::v-deep tbody {
  max-height: none;
}
@media (max-width: 600px) {
  .file-process-table ::v-deep tbody {
    max-height: none;
  }
}
.file-process-table ::v-deep tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
  cursor: pointer;
}
.file-process-table ::v-deep tbody::-webkit-scrollbar {
  width: 4px;
}
.file-process-table ::v-deep tbody::-webkit-scrollbar-track {
  background: transparent;
}
.file-process-table ::v-deep tbody::-webkit-scrollbar-thumb {
  background: #d0d5dd;
  border-radius: 8px;
}
.file-process-table ::v-deep tbody::-webkit-scrollbar-thumb:hover {
  background: #b8bec8;
}
.file-process-table ::v-deep tbody::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}
.file-process-table ::v-deep th,
.file-process-table ::v-deep td {
  font-size: 12px !important;
  height: 36px !important;
  padding: 4px 8px !important;
  vertical-align: middle !important;
  text-align: right !important;
}
.file-process-table ::v-deep th {
  color: #78909c !important;
  font-weight: 600 !important;
  white-space: nowrap;
  background: #fafbfc !important;
  box-shadow: 0 1px 0 #e5e9f0;
}
.file-process-col-check {
  width: 40px;
  max-width: 40px;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.file-process-check {
  margin: 0 !important;
  padding: 0 !important;
}
.file-process-check ::v-deep .v-input__slot {
  margin: 0 !important;
  justify-content: center;
}
.file-process-check ::v-deep .v-input--selection-controls__input {
  margin: 0 !important;
}
.file-process-table ::v-deep tr.file-process-row-selected > td {
  background: #f5f8fc !important;
}
.file-process-col-index {
  width: 36px;
  color: #b0b6c0 !important;
}
.file-process-col-file {
  width: 20%;
  min-width: 0;
}
.file-process-col-percent {
  width: 148px;
  min-width: 148px;
  white-space: nowrap;
}
.file-process-table ::v-deep td.file-process-col-percent {
  height: auto !important;
  min-height: 48px;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.file-process-progress {
  width: 100%;
  min-width: 120px;
  direction: ltr;
}
.file-process-progress-meta {
  margin-bottom: 5px;
}
.file-process-progress-percent {
  font-size: 12px;
  font-weight: 700;
  color: #1976d2;
  flex-shrink: 0;
  line-height: 1;
}
.file-process-progress ::v-deep .v-progress-linear {
  border: 1px solid #90caf9;
  border-radius: 999px;
  overflow: hidden;
  background: #f5f8fc !important;
}
.file-process-progress ::v-deep .v-progress-linear__background {
  background-color: #f5f8fc !important;
  opacity: 1 !important;
}
.file-process-col-count {
  width: 128px;
  white-space: nowrap;
}
.file-process-table ::v-deep td.file-process-col-count,
.file-process-table ::v-deep td.file-process-col-user,
.file-process-table ::v-deep td.file-process-col-date,
.file-process-table ::v-deep td.file-process-col-errors {
  padding-right: 17px !important;
}
.file-process-col-user {
  width: 100px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-process-col-date {
  width: 132px;
  min-width: 132px;
  white-space: nowrap;
  color: #607080 !important;
  font-size: 12px;
}
.file-process-col-status {
  width: 112px;
  white-space: nowrap;
}
.file-process-col-errors {
  width: 64px;
  max-width: 64px;
  white-space: nowrap;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.file-process-file-name {
  max-width: 100%;
  font-weight: 500;
  color: #455a64;
  direction: ltr;
  text-align: right;
  margin: 0;
}
.file-process-error-chip {
  cursor: pointer;
  min-width: 36px;
  justify-content: center;
}
.file-process-no-error {
  color: #b0b6c0;
  font-size: 12px;
}
.file-process-errors-card {
  border-radius: 4px !important;
  overflow: hidden;
}
.file-process-errors-head {
  padding: 10px 12px 6px;
  border-bottom: 1px solid #f0f2f5;
  gap: 8px;
}
.file-process-errors-head-text {
  min-width: 0;
  flex: 1;
}
.file-process-errors-title {
  font-size: 14px;
  font-weight: 700;
  color: #c62828;
  line-height: 1.3;
}
.file-process-errors-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: #78909c;
  max-width: 100%;
}
.file-process-errors-body {
  padding: 12px !important;
  overflow: hidden;
}
.file-process-errors-empty {
  padding: 32px 0;
  color: #90a4ae;
  font-size: 13px;
}
.file-process-errors-table {
  background: transparent;
  border-collapse: collapse;
  border-radius: 4px;
  overflow: hidden;
  border: none;
  box-shadow: 0 0 0 0.5px #e6eaef;
}
.file-process-errors-table ::v-deep .v-data-table__wrapper {
  overflow: hidden !important;
  border-radius: 4px;
}
.file-process-errors-table ::v-deep table {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
}
.file-process-errors-table ::v-deep thead {
  display: table;
  width: 100%;
  table-layout: fixed;
  flex-shrink: 0;
}
.file-process-errors-table ::v-deep thead th {
  background: #fafbfc !important;
  color: #607d8b !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border: 1px solid #eef1f5 !important;
  white-space: nowrap;
  padding: 6px 8px !important;
}
.file-process-errors-table ::v-deep thead th:first-child {
  border-top-right-radius: 4px;
}
.file-process-errors-table ::v-deep thead th:last-child {
  border-top-left-radius: 4px;
}
.file-process-errors-table ::v-deep tbody {
  display: block;
  /* exactly 10 rows visible without scroll */
  max-height: calc(36px * 10 + 2px);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(176, 182, 192, 0.55) transparent;
}
.file-process-errors-table ::v-deep tbody tr {
  display: table;
  width: 100%;
  table-layout: fixed;
}
.file-process-errors-table ::v-deep tbody::-webkit-scrollbar {
  width: 3px;
}
.file-process-errors-table ::v-deep tbody::-webkit-scrollbar-track {
  background: transparent;
}
.file-process-errors-table ::v-deep tbody::-webkit-scrollbar-thumb {
  background: rgba(176, 182, 192, 0.55);
  border-radius: 8px;
}
.file-process-errors-table ::v-deep tbody::-webkit-scrollbar-thumb:hover {
  background: rgba(158, 166, 176, 0.75);
}
.file-process-errors-table ::v-deep tbody::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
}
.file-process-errors-table ::v-deep tbody td {
  font-size: 12.5px;
  color: #455a64;
  border: 1px solid #f3f5f8 !important;
  vertical-align: middle;
  padding: 6px 8px !important;
  height: 36px;
}
.file-process-errors-table ::v-deep tbody tr:last-child td:first-child {
  border-bottom-right-radius: 4px;
}
.file-process-errors-table ::v-deep tbody tr:last-child td:last-child {
  border-bottom-left-radius: 4px;
}
.file-process-errors-table ::v-deep tbody tr:nth-child(even) {
  background: #fff;
}
.file-process-errors-col-index {
  width: 44px;
  white-space: nowrap;
  color: #90a4ae !important;
}
.file-process-errors-col-row {
  width: 92px;
  white-space: nowrap;
}
.file-process-errors-col-field {
  width: 106px;
  max-width: 136px;
  word-break: break-word;
}
.file-process-errors-col-msg {
  min-width: 160px;
  word-break: break-word;
}
.file-process-errors-row-badge {
  display: inline-block;
  min-width: 28px;
  padding: 1px 6px;
  border-radius: 6px;
  background: #ffebee;
  color: #c62828;
  font-weight: 600;
  font-size: 12px;
  text-align: center;
}
.file-process-errors-pagination {
  margin: 0;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-bottom: none;
  padding: 10px 12px 16px;
}
</style>

<style>
.file-process-sort-menu-content {
  min-width: 180px !important;
  max-width: 220px !important;
  max-height: 220px !important;
  overflow-y: auto !important;
  border-radius: 6px !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.file-process-sort-menu-content::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
.file-process-sort-menu-content .v-list {
  padding: 4px 0 !important;
}
.file-process-sort-menu-content .v-list-item {
  min-height: 32px !important;
  padding: 0 10px !important;
}
.file-process-sort-menu-content .v-list-item__title {
  font-size: 12px !important;
  line-height: 1.3;
  white-space: nowrap;
}
</style>
